import { createContext, useEffect, useState, use, useCallback } from 'react';
import type { ReactNode } from 'react';
import { DBCollection } from '@/lib/utils/constants-shared';

interface PlacesByPassageDataContextType {
  placesByPassage: PlacesByPassage[];
  getPlacesByPassageByPassageId: (passageId: string) => PlacesByPassage | undefined;
  loadPlacesByPassageByPassageId: (passageId: string) => Promise<PlacesByPassage | undefined>;
}

export const PlacesByPassageDataContext = createContext<PlacesByPassageDataContextType | undefined>(undefined);

export function usePlacesByPassageData(passageId?: string) {
  const context = use(PlacesByPassageDataContext);
  if (!context) {
    throw new Error('usePlacesByPassageData must be used within PlacesByPassageDataProvider');
  }

  const [data, setData] = useState<PlacesByPassage | undefined>();
  const [isLoading, setIsLoading] = useState(false);

  useEffect(() => {
    if (!passageId) return;

    // Check memory cache
    const cached = context.getPlacesByPassageByPassageId(passageId);
    if (cached) {
      setData(cached);
      return;
    }

    // Load on-demand
    setIsLoading(true);
    context.loadPlacesByPassageByPassageId(passageId)
      .then(loaded => {
        setData(loaded);
        setIsLoading(false);
      })
      .catch(err => {
        console.error(`Failed to load places mapping for ${passageId}:`, err);
        setIsLoading(false);
      });
  }, [passageId, context]);

  return {
    data,
    isLoading,
    ...context
  };
}

interface PlacesByPassageDataProviderProps {
  children: ReactNode;
  placesByPassage: PlacesByPassage[];
}

import { LazyDataLoader } from '@/lib/utils/lazy-data-loader';

export function PlacesByPassageDataProvider({ children, placesByPassage: initialData }: PlacesByPassageDataProviderProps) {
  const [loader] = useState(() => new LazyDataLoader<PlacesByPassage>(DBCollection.placesByPassage, 'passageId'));
  const [cachedItems, setCachedItems] = useState<PlacesByPassage[]>([]);

  // Preload any initial data into memory cache
  useEffect(() => {
    if (initialData && initialData.length > 0) {
      loader.preload(initialData);
      setCachedItems(loader.getAllCached());
    }
  }, [initialData, loader]);

  const getPlacesByPassageByPassageId = useCallback((passageId: string): PlacesByPassage | undefined => {
    return cachedItems.find(item => item.passageId === passageId);
  }, [cachedItems]);

  const loadPlacesByPassageByPassageId = useCallback(async (passageId: string): Promise<PlacesByPassage | undefined> => {
    const item = await loader.get(passageId);
    if (item) {
      setCachedItems(loader.getAllCached());
    }
    return item;
  }, [loader]);

  return (
    <PlacesByPassageDataContext.Provider
      value={{
        placesByPassage: cachedItems,
        getPlacesByPassageByPassageId,
        loadPlacesByPassageByPassageId
      }}
    >
      {children}
    </PlacesByPassageDataContext.Provider>
  );
}

